import { useState } from 'react';

import { Notice } from '@/components/Modal';
import { EmptyCard, LoadingBlock } from '@/components/PageHeader';
import { SiteSearchSelect } from '@/components/SiteSearchSelect';
import type { Site } from '@/lib/types';
import { OrgChartBoard } from '@/pages/hr/OrgChartBoard';
import { useOrgChart } from '@/pages/hr/useOrgChart';
import {
  useDecideOrgChange,
  useOrgChartChange,
  useOrgLayout,
  useSaveOrgLayout,
  useSubmitOrgChange,
  type OrgCandidate,
  type OrgGroup,
  type OrgLayout,
} from '@/pages/hr/useOrgLayout';
import { SITE_SECTIONS } from '@/pages/sites/status/siteStatusData';

/**
 * 이 현장에 배치는 됐는데 <b>조직도 자리에 서지 않은 사람</b>.
 *
 * <p>조직도는 자리에 세워야 보인다. 전술판에서 배치해도 누군가 자리를 눌러 세우기 전까지 판 위에
 * 없고, 그동안 화면 어디에도 빠졌다는 표시가 없었다 — 조직도를 보고 인원을 세면 그 사람이
 * 사라진다. 실제로 하노이에서 안전 파견 한 명이 그렇게 빠져 있었다(안전 구간을 안 골라 자리가
 * 없었다).
 */
function unplacedOf(layout: OrgLayout | undefined): OrgCandidate[] {
  if (!layout) return [];
  const standing = new Set<string>();
  for (const group of layout.groups) {
    for (const slot of group.slots) {
      if (slot.personnelId) standing.add(slot.personnelId);
    }
  }
  return layout.members.filter((member) => !standing.has(member.personnelId));
}

/**
 * 현장별 조직도 (DOC-50 §6 · V379).
 *
 * <h2>양식이 화면이다 (사용자 지시 2026-10-01)</h2>
 * 조직도는 회사가 쓰는 <b>엑셀 한 장</b>이 정본이고(docs/design/조직도), 화면은 그 양식을 그대로
 * 세운다 — 묶음의 회색 머리칸, 그 아래 자리 줄, 현장소장에서 내려오는 선까지. 직종별 카드를
 * 격자에 흩어 놓던 예전 보기는 <b>같은 자료를 다른 모양으로</b> 말했고, 그래서 인쇄해서 쓰는
 * 양식과 화면이 서로 다른 조직을 그렸다.
 *
 * <h2>머리줄은 남긴다</h2>
 * 「상주 N명 / 기준 N명」 은 양식에 없는 값이지만 <b>판정</b>이다 — 기준인원표와 견준 결과라
 * 조직도를 보는 사람이 가장 먼저 묻는 것이기도 하다. 기준이 없는 현장은 그 줄이 그냥 나오지
 * 않는다(판정하지 않는다).
 */
export function OrgChartView({
  sites,
  initialSiteId = null,
}: {
  /**
   * 국내 진행 · 해외 진행 · 미진행 · 평가 · 준공으로 묶어 검색해 고른다(사용자 지시 2026-10-16).
   * 준공 현장은 떠난 사람도 이름이 남는 판이다.
   */
  sites: Site[];
  /** 할일의 변경 승인 요청이 가리키는 현장(V452). 목록에 없으면 첫 현장이다. */
  initialSiteId?: string | null;
}) {
  const [siteId, setSiteId] = useState<string>(
    sites.find((site) => site.id === initialSiteId)?.id ??
      SITE_SECTIONS.map((section) => sites.find(section.match)).find(Boolean)?.id ??
      sites[0]?.id ??
      '',
  );

  if (sites.length === 0) return <EmptyCard>현장이 없습니다.</EmptyCard>;

  return (
    <div className="space-y-3">
      <div className="card p-4 no-print flex items-center gap-2 flex-wrap">
        <SiteSearchSelect sites={sites} value={siteId} onChange={setSiteId} />
      </div>

      <SiteOrgChart key={siteId} siteId={siteId || null} former />
    </div>
  );
}

/**
 * 현장 <b>하나</b>의 조직도. 현장 조회 › 공통 › 조직도 탭이 이것을 그대로 쓴다
 * (사용자 지시 2026-09-28) — 현장이 이미 정해져 있어 고르는 칸이 없다.
 *
 * <p>인력배치표의 조직도와 <b>같은 컴포넌트</b>다. 두 벌이면 자리를 세는 규칙이 언젠가 한쪽만
 * 고쳐지고, 그날 두 화면이 같은 현장을 다르게 말한다.
 *
 * @param readOnly 현장 조회의 조직도 탭이 켜는 값(사용자 지시 2026-10-01). 판을 고치는 자리는
 *                 <b>인사관리 › 인력배치표 › 조직도</b> 하나다 — 두 화면에서 같은 판을 고칠 수
 *                 있으면 서로의 저장을 덮어쓰고, 현장 탭은 보러 오는 자리다. 예외는 그 현장의
 *                 현장대리인 하나다 — 고친 판은 변경 요청으로만 올라간다(V452).
 * @param former   종결 현장의 떠난 사람과 「발령 · 전출」 까지. 인력배치표 › 조직도만 켠다 — 현장 조회 ·
 *                 직원찾기는 지금 배치만 보인다(사용자 지시 2026-10-16).
 */
export function SiteOrgChart({
  siteId,
  readOnly = false,
  former = false,
}: {
  siteId: string | null;
  readOnly?: boolean;
  former?: boolean;
}) {
  const chart = useOrgChart(siteId, former);
  /*
    같은 열쇠의 조회라 판을 그리는 쪽({@code OrgChartBoard})과 <b>한 번만</b> 불러온다. 머리줄에
    미배치를 적자고 따로 부르지 않는다.
  */
  const layout = useOrgLayout(siteId, former);
  const save = useSaveOrgLayout(siteId, former);

  /*
    변경 요청 (V452 · 사용자 지시 2026-10-14). 현장 조회 탭은 읽기 전용이지만 <b>그 현장의 현장대리인</b>은
    여기서 판을 고친다 — 고친 것은 초안({@code draft})에만 남고, [변경 요청] 을 눌러야 서버에 요청 한
    줄로 앉는다. 판이 바뀌는 것은 인사배치 담당이 인력배치표 › 조직도에서 승인할 때다.
  */
  const change = useOrgChartChange(siteId);
  const submit = useSubmitOrgChange(siteId);
  const decide = useDecideOrgChange(siteId);
  const pending = change.data?.pending ?? null;
  const proposing = readOnly && !!change.data?.canPropose;
  const [draft, setDraft] = useState<OrgGroup[] | null>(null);
  const [showRequested, setShowRequested] = useState(false);

  // 대리인은 자기 초안 → 대기 중인 요청 → 지금 판 순으로 본다. 승인하는 쪽은 [요청안 보기] 를 눌렀을 때만 요청안을 본다.
  const shownGroups = proposing
    ? (draft ?? pending?.groups)
    : !readOnly && showRequested && pending
      ? pending.groups
      : undefined;
  const unplaced = unplacedOf(layout.data && shownGroups ? { ...layout.data, groups: shownGroups } : layout.data);

  const approve = () => {
    if (!pending) return;
    if (!window.confirm('요청한 판으로 조직도를 바꿀까요?')) return;
    decide.mutate({ id: pending.id, decision: 'approve' }, { onSuccess: () => setShowRequested(false) });
  };

  return (
    <div className="space-y-3">
      {chart.isPending ? (
        <LoadingBlock />
      ) : chart.error ? (
        <Notice tone="error">조직도를 불러오지 못했습니다.</Notice>
      ) : chart.data ? (
        <header className="card p-4 flex items-baseline gap-2 flex-wrap">
          <h2 style={{ fontSize: '16px', fontWeight: 700 }}>{chart.data.siteName}</h2>
          {chart.data.facilityType && (
            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{chart.data.facilityType}</span>
          )}
          {chart.data.scaleLabel && (
            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{chart.data.scaleLabel}</span>
          )}
          <span className="ml-auto" style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
            상주 {chart.data.totalResident}명
            {chart.data.standardFound && ` / 기준 ${chart.data.totalRequired}명`}
            {chart.data.totalVisiting > 0 && ` · 겸직·파견 ${chart.data.totalVisiting}명`}
            {/*
              <b>미배치</b> — 이 현장에 배치는 됐는데 조직도 자리에 서지 않은 사람
              (사용자 지시 2026-10-01). 상주·기준·겸직과 같은 줄에 있어야 「이 현장 인원」 을
              셀 때 함께 읽힌다.

              <p><b>여기서는 숫자만</b> 적는다. 이름과 «세우는 손» 은 판 위에 있다
                ({@code OrgChartBoard} 의 미배치 줄) — 누를 수 없는 이름을 여기 늘어놓으면
                보고도 할 수 있는 일이 없다.

              <p>빨갛게 칠하지 않는다. 사람 이름에 빨강을 쓰면 <b>그 사람이 잘못한 것</b>처럼
                읽힌다 — 빠진 것은 자리이지 사람이 아니다(사용자 지적 2026-10-01).
            */}
            {unplaced.length > 0 && ` · 미배치 ${unplaced.length}명`}
          </span>
          {layout.data && proposing && (
            <FireSafetyToggle
              groups={shownGroups ?? layout.data.groups}
              onChange={setDraft}
              disabled={submit.isPending}
            />
          )}
          {layout.data && !readOnly && !showRequested && (
            <FireSafetyToggle
              groups={layout.data.groups}
              onChange={(groups) => save.mutate(groups)}
              disabled={save.isPending}
              failed={!!save.error}
            />
          )}
        </header>
      ) : null}

      {proposing && (
        <div className="card p-3 no-print flex items-center gap-2 flex-wrap" style={{ fontSize: '12px' }}>
          {pending && (
            <span style={{ color: 'var(--text-secondary)' }}>
              승인 대기 · {pending.requesterName ?? ''} {formatRequestedAt(pending.requestedAt)}
            </span>
          )}
          {submit.error && <span style={{ color: 'var(--danger)' }}>요청하지 못했습니다</span>}
          <span className="ml-auto flex gap-2">
            <button
              type="button"
              className="btn-secondary"
              style={{ fontSize: '12px', padding: '4px 12px' }}
              disabled={!draft || submit.isPending}
              onClick={() => setDraft(null)}
            >
              되돌리기
            </button>
            <button
              type="button"
              className="btn-primary"
              style={{ fontSize: '12px', padding: '4px 12px' }}
              disabled={!draft || submit.isPending}
              onClick={() => draft && submit.mutate(draft, { onSuccess: () => setDraft(null) })}
            >
              변경 요청
            </button>
          </span>
        </div>
      )}

      {!readOnly && pending && (
        <div className="card p-3 no-print flex items-center gap-2 flex-wrap" style={{ fontSize: '12px' }}>
          <span style={{ color: 'var(--text-secondary)' }}>
            변경 요청 · {pending.requesterName ?? ''} {formatRequestedAt(pending.requestedAt)}
          </span>
          {decide.error && <span style={{ color: 'var(--danger)' }}>처리하지 못했습니다</span>}
          <span className="ml-auto flex gap-2">
            <button
              type="button"
              className="btn-secondary"
              style={{ fontSize: '12px', padding: '4px 12px' }}
              onClick={() => setShowRequested((shown) => !shown)}
            >
              {showRequested ? '현재 조직도 보기' : '요청안 보기'}
            </button>
            <button
              type="button"
              className="btn-secondary"
              style={{ fontSize: '12px', padding: '4px 12px' }}
              disabled={decide.isPending}
              onClick={() =>
                decide.mutate({ id: pending.id, decision: 'reject' }, { onSuccess: () => setShowRequested(false) })
              }
            >
              반려
            </button>
            <button
              type="button"
              className="btn-primary"
              style={{ fontSize: '12px', padding: '4px 12px' }}
              disabled={decide.isPending}
              onClick={approve}
            >
              승인
            </button>
          </span>
        </div>
      )}

      <OrgChartBoard
        siteId={siteId}
        former={former}
        readOnly={proposing ? false : readOnly || (showRequested && !!pending)}
        groups={shownGroups}
        onGroupsChange={proposing ? setDraft : undefined}
      />
    </div>
  );
}

const FIRE_SAFETY = '소방안전관리자';
/** 아래 묶음의 선이 내려오는 자리 (현장소장 복원 2026-10-28 · 대리인은 그 옆에 선 없이 선다). */
const HEAD = '현장소장';
/** 양식 셋째 줄의 빈 가운데 칸 — 건축(2)과 토목/조경(4) 사이. */
const FIRE_SAFETY_COL = 3;
const FIRE_SAFETY_ROW = 3;

/**
 * 「소방안전관리자」 칸 켜고 끄기 (사용자 지시 2026-10-01).
 *
 * <p>현장에 따라 소방안전관리자를 두기도 하고 안 두기도 한다. 양식에 늘 박아 두면 없는 현장마다
 * 빈 칸이 서고, 양식에서 빼면 있는 현장이 세울 자리가 없다. 그래서 머리줄의 스위치 하나로 세우고
 * 걷는다 — 켜면 건축과 토목/조경 사이(셋째 줄 3번 칸)에 현장소장 아래로 선다.
 *
 * <p>켜고 끄는 것은 <b>판 저장</b>이다({@link useSaveOrgLayout}) — 묶음 하나를 더하거나 빼서
 * 통째로 보낸다. 서버의 틀 맞추기는 양식에 없는 묶음을 그대로 두므로 다음에 열어도 남아 있다.
 * 끌 때 그 칸에 사람이 서 있으면 이름을 적어 묻는다 — 칸과 함께 그 사람의 자리도 사라진다.
 */
function FireSafetyToggle({
  groups,
  onChange,
  disabled,
  failed = false,
}: {
  groups: OrgGroup[];
  /** 판 저장이거나(인력배치표) 현장대리인의 초안이다(현장 조회 탭 · V452). */
  onChange: (groups: OrgGroup[]) => void;
  disabled: boolean;
  failed?: boolean;
}) {
  const group = groups.find((item) => item.title === FIRE_SAFETY);

  const toggle = () => {
    if (group) {
      const standing = group.slots.filter((slot) => slot.personnelId);
      if (standing.length > 0) {
        const names = standing.map((slot) => slot.personName ?? '?').join(', ');
        if (!window.confirm(`${names} 님이 선 「${FIRE_SAFETY}」 칸을 지울까요?`)) return;
      }
      onChange(groups.filter((item) => item !== group));
      return;
    }

    const parent = groups.find((item) => item.title === HEAD);
    const added: OrgGroup = {
      id: null,
      parentId: parent?.id ?? null,
      title: FIRE_SAFETY,
      kind: 'FREE',
      preset: null,
      gridCol: FIRE_SAFETY_COL,
      gridRow: FIRE_SAFETY_ROW,
      sortOrder: Math.max(0, ...groups.map((item) => item.sortOrder)) + 1,
      slots: [{ id: null, title: FIRE_SAFETY, sortOrder: 0, personnelId: null, personName: null, personRank: null, hasPhoto: false }],
    };
    onChange([...groups, added]);
  };

  return (
    <label
      className="no-print"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        marginLeft: '12px',
        fontSize: '12px',
        color: 'var(--text-secondary)',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      <input type="checkbox" checked={!!group} disabled={disabled} onChange={toggle} />
      {FIRE_SAFETY}
      {failed && <span style={{ color: 'var(--danger)' }}>저장하지 못했습니다</span>}
    </label>
  );
}

/** 「10. 14. 오후 03:20」 — 언제 올린 요청인지. */
function formatRequestedAt(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleString('ko-KR', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}
